import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowRight, Lightning, QrCode, Globe, Check, Sparkle, ShieldCheck, Clock, CaretDown,
  Wallet, SignIn, UserCirclePlus, Airplane, CurrencyEur, Plus
} from "@phosphor-icons/react";
import api from "../lib/api";
import { flag, fmtEUR, countryName } from "../lib/format";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsFeed from "../components/NewsFeed";
import Testimonials from "../components/Testimonials";
import { useAuth } from "../context/AuthContext";
import useCountUp from "../lib/useCountUp";

/* Locale-driven hero imagery (watermark-free images.unsplash.com, no premium).
 * Nordic default, Mediterranean for southern-EU / Arabic, Asia for CJK. */
const HERO_BY_BUCKET = {
  // Nordic travel flat-lay: Fjällräven backpack, Ordnance Survey map, Field Notes, Nikon D4S
  nordic: {
    url: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    alt: "Nordic travel essentials: map, camera and backpack",
  },
  // Santorini caldera — whitewashed buildings, blue sea
  mediterranean: {
    url: "https://images.unsplash.com/photo-1510414842594-a61c69b5ae57?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    alt: "Mediterranean coastline with whitewashed village",
  },
  // Tokyo Akihabara neon street
  asia: {
    url: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    alt: "Tokyo neon-lit street at night",
  },
};
const LOCALE_BUCKET = {
  es: "mediterranean", el: "mediterranean", it: "mediterranean",
  pt: "mediterranean", ar: "mediterranean", tr: "mediterranean",
  zh: "asia", ja: "asia", ko: "asia",
};

/** Per-language secondary CTA in the hero — a localized offer link that
 *  channels users to a country/region plan page relevant to their language.
 *  Guests clicking "How it works" can still scroll down one section to the
 *  Explain band, so losing that default button is fine. Languages not listed
 *  here keep the generic "How it works" anchor. */
const HERO_LOCAL_CTA = {
  el: { labelKey: "home.heroLocalEl", href: "/plans?country=GR" },
  es: { labelKey: "home.heroLocalEs", href: "/plans?country=ES" },
  it: { labelKey: "home.heroLocalIt", href: "/plans?country=IT" },
  tr: { labelKey: "home.heroLocalTr", href: "/plans?country=TR" },
  ar: { labelKey: "home.heroLocalAr", href: "/plans?country=MA" },
  ja: { labelKey: "home.heroLocalJa", href: "/plans?country=JP" },
  zh: { labelKey: "home.heroLocalZh", href: "/plans?country=CN" },
  fr: { labelKey: "home.heroLocalFr", href: "/plans?country=FR" },
  de: { labelKey: "home.heroLocalDe", href: "/plans?country=DE" },
};

/* ===== tiny building blocks ===== */

function FAQItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`card-soft !rounded-2xl overflow-hidden transition-shadow ${open ? "shadow-pop" : ""}`}
      data-testid={testid}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
      >
        <span className="font-display font-semibold text-lg tracking-tight">{q}</span>
        <CaretDown
          size={18}
          weight="bold"
          className={`transition-transform ${open ? "rotate-180 text-cobalt-500" : "text-ink-500"}`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="px-6 pb-5 text-ink-700 leading-relaxed">{a}</div>
        </div>
      </div>
    </div>
  );
}

function Stat({ to, suffix = "", label, testid }) {
  const [v, ref] = useCountUp(to);
  return (
    <div
      ref={ref}
      className="text-center md:text-left border-t md:border-t-0 md:border-l first:border-l-0 first:border-t-0 border-white/15 pt-5 md:pt-0 md:pl-8 first:pl-0"
      data-testid={testid}
    >
      <div className="font-display font-bold text-4xl lg:text-5xl tracking-tight tabular-nums">
        {v}
        {suffix}
      </div>
      <div className="mt-1 text-sm text-white/60 uppercase tracking-widest">{label}</div>
    </div>
  );
}

/** Interactive mock wallet that animates a top-up → balance increase. */
function WalletDemo() {
  const [balance, setBalance] = useState(18.4);
  const [pulsing, setPulsing] = useState(false);
  const topup = (amount) => {
    setPulsing(true);
    setBalance((b) => Math.min(500, +(b + amount).toFixed(2)));
    setTimeout(() => setPulsing(false), 600);
  };
  return (
    <div
      className="relative bg-white/5 backdrop-blur rounded-3xl p-6 border border-white/10 shadow-pop"
      data-testid="wallet-demo"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="inline-flex items-center gap-2 pill bg-white/10 !text-white/80 text-xs">
          <Wallet size={14} weight="fill" /> Live demo
        </div>
        <span className="w-2 h-2 rounded-full bg-mint animate-pulse" />
      </div>
      <div className="text-white/60 text-xs uppercase tracking-widest">Wallet balance</div>
      <div
        className={`font-display font-bold text-5xl tracking-tight mt-1 tabular-nums transition-all ${pulsing ? "text-mint scale-[1.04]" : "text-white"}`}
      >
        €{balance.toFixed(2)}
      </div>
      <div className="mt-4 text-xs text-white/60">
        ≈ {(balance / 2.2).toFixed(1)} GB of Europe data at local rates
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2" data-testid="wallet-demo-topups">
        {[10, 25, 50].map((a) => (
          <button
            key={a}
            onClick={() => topup(a)}
            className="group rounded-2xl bg-white/10 hover:bg-white/20 text-white px-3 py-3 text-sm font-semibold inline-flex items-center justify-center gap-1 transition"
            data-testid={`wallet-demo-topup-${a}`}
          >
            <Plus size={14} weight="bold" className="opacity-70 group-hover:opacity-100" />
            €{a}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-white/50 mt-3 text-center">Try it — no sign-up needed.</p>
    </div>
  );
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [countries, setCountries] = useState([]);
  const [liveCount, setLiveCount] = useState(347);

  const hero = HERO_BY_BUCKET[LOCALE_BUCKET[i18n.language] || "nordic"] || HERO_BY_BUCKET.nordic;
  const localCta = HERO_LOCAL_CTA[(i18n.language || "en").split("-")[0]];

  useEffect(() => {
    api.get("/plans/countries").then(({ data }) => setCountries(data)).catch(() => {});
  }, []);

  // Gently jitter the live-counter every 4s for a living-site feel
  useEffect(() => {
    const id = setInterval(() => {
      setLiveCount((c) => Math.max(210, Math.min(620, c + Math.floor((Math.random() - 0.4) * 9))));
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const featured = countries.slice(0, 8);
  const totalPlans = countries.reduce((a, c) => a + c.plan_count, 0) || 500;
  const totalCountries = Math.max(countries.length, 120);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Header />

      {/* ========= HERO ========= */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-cobalt-200/60 blob animate-blob pointer-events-none" />
        <div className="absolute top-40 -right-40 w-[520px] h-[520px] bg-coral-soft/70 blob animate-blob pointer-events-none" style={{ animationDelay: "-6s" }} />
        <div className="absolute bottom-0 left-1/3 w-[320px] h-[320px] bg-mint-soft/60 blob animate-blob pointer-events-none" style={{ animationDelay: "-3s" }} />

        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 lg:pt-28 pb-20 relative">
          <div className="grid lg:grid-cols-[1.1fr_1fr] items-center gap-12 lg:gap-16">
            <div className="animate-fade-up">
              <div className="inline-flex items-center gap-2 pill bg-white shadow-soft border border-ink-200 mb-6" data-testid="hero-badge">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-mint opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-mint" />
                </span>
                <span className="text-ink-700 tabular-nums">
                  <span className="font-semibold text-ink-950">{liveCount}</span> {t("home.heroLivePill", "travelers connecting right now")}
                </span>
              </div>
              <h1 className="font-display font-bold text-[2.8rem] sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-ink-950 whitespace-pre-line" data-testid="hero-title">
                {t("home.heroTitle")}
              </h1>
              <p className="mt-7 text-lg md:text-xl text-ink-700 max-w-xl leading-relaxed" data-testid="hero-sub">
                {t("home.heroSub")}
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/plans" className="btn-primary text-base group" data-testid="hero-cta-primary">
                  {t("home.cta")}
                  <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </Link>
                {localCta ? (
                  <Link
                    to={localCta.href}
                    className="btn-coral text-base group"
                    data-testid="hero-cta-local"
                  >
                    <Sparkle size={16} weight="fill" />
                    {t(localCta.labelKey)}
                    <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                  </Link>
                ) : (
                  <a href="#explain" className="btn-outline text-base" data-testid="hero-cta-secondary">
                    {t("home.ctaSecondary")}
                  </a>
                )}
              </div>
              <div className="mt-10 flex items-center gap-6 text-sm text-ink-700">
                <div className="flex items-center gap-1.5"><Clock size={16} className="text-cobalt-500" weight="bold" /> 90s</div>
                <div className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-mint" weight="bold" /> Stripe secure</div>
                <div className="flex items-center gap-1.5"><Globe size={16} className="text-coral" weight="bold" /> 120+ countries</div>
              </div>
            </div>

            {/* Hero visual */}
            <div className="relative animate-fade-up" style={{ animationDelay: "0.2s" }}>
              <div className="relative aspect-[4/5] max-w-md mx-auto">
                <div className="absolute inset-0 rounded-[3rem] overflow-hidden shadow-pop">
                  <img src={hero.url} alt={hero.alt} className="w-full h-full object-cover transition-opacity duration-500" key={hero.url} />
                </div>
                <div className="absolute -left-6 bottom-8 bg-white rounded-3xl shadow-soft p-5 flex items-center gap-3 animate-float">
                  <div className="w-14 h-14 bg-cobalt-50 rounded-2xl flex items-center justify-center">
                    <QrCode size={28} weight="bold" className="text-cobalt-500" />
                  </div>
                  <div>
                    <div className="overline text-[10px]">{t("common.bubbleReady")}</div>
                    <div className="font-display font-bold">{t("common.bubblePlanShort")}</div>
                  </div>
                </div>
                <div className="absolute -right-4 top-10 bg-white rounded-3xl shadow-soft p-4 animate-float" style={{ animationDelay: "-3s" }}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-2.5 h-2.5 rounded-full bg-mint animate-pulse" />
                    <span className="font-semibold">{t("common.bubbleConnected")}</span>
                  </div>
                  <div className="text-xs text-ink-500 mt-0.5">{t("common.bubbleNetwork")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* stat band — animated count-up */}
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
          <div className="bg-ink-950 text-white rounded-[2.5rem] px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            <Stat to={totalCountries} suffix="+" label={t("home.statCountries")} testid="stat-countries" />
            <Stat to={totalPlans} suffix="+" label={t("home.statPlans")} testid="stat-plans" />
            <Stat to={90} suffix="s" label={t("home.statActivation")} testid="stat-activation" />
            <div className="text-center md:text-left border-t md:border-t-0 md:border-l border-white/15 pt-5 md:pt-0 md:pl-8" data-testid="stat-support">
              <div className="font-display font-bold text-4xl lg:text-5xl tracking-tight">24/7</div>
              <div className="mt-1 text-sm text-white/60 uppercase tracking-widest">{t("home.statSupport")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ========= POPULAR DESTINATIONS ========= */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div className="max-w-2xl">
              <div className="overline mb-3">{t("home.popularEyebrow")}</div>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">
                {t("home.popularTitle")}
              </h2>
            </div>
            <Link to="/plans" className="btn-ghost" data-testid="link-all-plans">
              {t("home.seeAll")} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
            {featured.map((c, i) => {
              const palette = ["bg-cobalt-50", "bg-coral-soft/50", "bg-mint-soft", "bg-sun-soft", "bg-berry-soft", "bg-sky-soft"];
              return (
                <Link
                  key={c.country_iso2}
                  to={`/plans?country=${c.country_iso2}`}
                  className="card-soft p-6 group transition-transform duration-300 hover:-translate-y-1"
                  data-testid={`country-card-${c.country_iso2}`}
                >
                  <div className={`w-14 h-14 rounded-2xl ${palette[i % palette.length]} flex items-center justify-center text-3xl mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[-4deg]`}>
                    {flag(c.country_iso2)}
                  </div>
                  <div className="font-display font-semibold text-xl tracking-tight">{countryName(c.country_iso2, i18n.language, c.country)}</div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-ink-500">{c.plan_count} {t("common.plans")}</span>
                    <span className="pill bg-cobalt-50 text-cobalt-600 transition-colors group-hover:bg-cobalt-500 group-hover:text-white">
                      {t("common.from")} {fmtEUR(c.min_price)}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========= EXPLAIN · Country plan vs Pay & Fly ========= */}
      <section id="explain" className="py-20 bg-gradient-to-b from-white to-cobalt-50/40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="overline mb-3">{t("home.explainEyebrow", "How BlueRoam works")}</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight" data-testid="explain-title">
              {t("home.explainTitle", "Two ways to stay connected abroad.")}
            </h2>
            <p className="mt-4 text-ink-700 text-lg leading-relaxed">
              {t("home.explainSub", "One fixed plan for one trip — or a wallet that follows you across every border.")}
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            {/* Country plans */}
            <div className="card-soft p-8 lg:p-10 relative overflow-hidden transition-transform duration-300 hover:-translate-y-1" data-testid="explain-option-country">
              <div className="absolute -top-16 -right-16 w-56 h-56 bg-cobalt-100 blob" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-cobalt-50 text-cobalt-500 flex items-center justify-center mb-5">
                  <Globe size={28} weight="bold" />
                </div>
                <div className="overline mb-2">{t("common.plans", "plans")} · 1 {t("common.country", "Country")}</div>
                <h3 className="font-display font-bold text-3xl tracking-tight">{t("home.explainOneTitle", "Country plans")}</h3>
                <p className="mt-3 text-ink-700 leading-relaxed">
                  {t("home.explainOneDesc", "Know exactly where you're going? One fixed bundle, one price, one QR. Done.")}
                </p>
                <ul className="mt-5 space-y-2.5">
                  {[
                    t("home.explainOneBullet1", "120+ countries & regional bundles"),
                    t("home.explainOneBullet2", "From 1 GB / 7 days up to unlimited 30-day plans"),
                    t("home.explainOneBullet3", "Emailed seconds after checkout"),
                  ].map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-ink-700">
                      <Check size={16} weight="bold" className="text-cobalt-500 mt-1 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/plans" className="btn-primary mt-7 group" data-testid="explain-country-cta">
                  {t("home.explainOneCta", "Browse country plans")}
                  <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>

            {/* Pay & Fly wallet */}
            <div className="card-soft p-8 lg:p-10 relative overflow-hidden bg-coral-soft/40 border border-coral-soft transition-transform duration-300 hover:-translate-y-1" data-testid="explain-option-payfly">
              <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-coral-soft/70 blob" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-white text-coral-deep flex items-center justify-center mb-5 shadow-soft">
                  <Wallet size={28} weight="bold" />
                </div>
                <div className="overline !text-coral-deep mb-2 inline-flex items-center gap-1.5">
                  <Airplane size={12} weight="fill" /> {t("common.payfly", "Pay & Fly")}
                </div>
                <h3 className="font-display font-bold text-3xl tracking-tight">{t("home.explainTwoTitle", "Pay & Fly wallet")}</h3>
                <p className="mt-3 text-ink-900 leading-relaxed">
                  {t("home.explainTwoDesc", "Top up once. Land in any of 120+ countries. Your balance pays local rates as you go.")}
                </p>
                <ul className="mt-5 space-y-2.5">
                  {[
                    t("home.explainTwoBullet1", "One eSIM, every border — no reinstall"),
                    t("home.explainTwoBullet2", "Transparent per-GB rates, only for what you use"),
                    t("home.explainTwoBullet3", "12-month validity · optional auto top-up"),
                  ].map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-ink-900">
                      <Check size={16} weight="bold" className="text-coral-deep mt-1 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <a href="#payfly" className="btn-coral mt-7 group" data-testid="explain-payfly-cta">
                  <Lightning size={16} weight="fill" /> {t("home.explainTwoCta", "See it in action")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========= PAY & FLY — single interactive band (replaces 3-step + wallet CTA) ========= */}
      <section id="payfly" className="py-20" data-testid="payfly-band">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="bg-ink-950 text-white rounded-[2.5rem] px-7 py-12 lg:px-14 lg:py-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-coral blob opacity-80" />
            <div className="absolute -bottom-24 -left-16 w-72 h-72 bg-cobalt-500 blob opacity-90" style={{ animationDelay: "-3s" }} />
            <div className="relative grid lg:grid-cols-[1.15fr_1fr] items-center gap-10 lg:gap-14">
              <div>
                <div className="overline !text-coral-soft mb-3 inline-flex items-center gap-2">
                  <Airplane size={14} weight="fill" /> {t("home.payflyBandEyebrow", "Pay & Fly")}
                </div>
                <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight leading-[1.05]">
                  {t("home.payflyBandTitle", "One wallet. Every border. Pay per GB.")}
                </h2>
                <p className="mt-5 text-white/75 max-w-xl text-lg leading-relaxed">
                  {t("home.payflyBandDescShort", "Top up, install one eSIM, and we debit at local rates as you roam. Try the demo →")}
                </p>

                {/* 3-step inline strip */}
                <ol className="mt-8 grid sm:grid-cols-3 gap-3" data-testid="payfly-steps-strip">
                  {[
                    { n: "1", icon: CurrencyEur, label: t("home.payflyStep1TitleShort", "Top up") },
                    { n: "2", icon: QrCode, label: t("home.payflyStep2TitleShort", "Install eSIM") },
                    { n: "3", icon: Airplane, label: t("home.payflyStep3TitleShort", "Fly & use") },
                  ].map((s) => {
                    const I = s.icon;
                    return (
                      <li
                        key={s.n}
                        className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-4 py-3"
                      >
                        <div className="w-8 h-8 rounded-full bg-white text-ink-950 flex items-center justify-center font-display font-bold text-sm">{s.n}</div>
                        <I size={18} weight="bold" className="text-white/70" />
                        <span className="font-semibold text-sm">{s.label}</span>
                      </li>
                    );
                  })}
                </ol>

                {/* CTA — auth-aware, placed once to avoid duplication with nav */}
                <div className="mt-8 flex flex-wrap gap-3">
                  {user ? (
                    <Link
                      to="/payfly"
                      className="btn bg-white text-ink-950 shadow-pop hover:bg-white/95 text-base"
                      data-testid="payfly-band-wallet"
                    >
                      <Wallet size={18} weight="bold" /> {t("home.walletCtaGo", "Open my wallet")}
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/register"
                        className="btn bg-white text-ink-950 shadow-pop hover:bg-white/95 text-base"
                        data-testid="payfly-band-register"
                      >
                        <UserCirclePlus size={18} weight="bold" /> {t("home.walletCtaCreateShort", "Start my wallet")}
                      </Link>
                      <Link
                        to="/login"
                        state={{ from: { pathname: "/payfly" } }}
                        className="btn bg-transparent text-white border border-white/30 hover:bg-white/10 text-base"
                        data-testid="payfly-band-login"
                      >
                        <SignIn size={18} weight="bold" /> {t("home.walletCtaLoginShort", "Log in")}
                      </Link>
                    </>
                  )}
                </div>
              </div>

              {/* Interactive wallet demo */}
              <WalletDemo />
            </div>
          </div>
        </div>
      </section>

      {/* ========= TESTIMONIALS ========= */}
      <Testimonials />

      {/* ========= NEWS ========= */}
      <NewsFeed compact />

      {/* ========= FAQ ========= */}
      <section id="faq" className="py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <div className="overline mb-3">{t("home.faqEyebrow")}</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">{t("home.faqTitle")}</h2>
          </div>
          <div className="space-y-3">
            <FAQItem q={t("home.faq1q")} a={t("home.faq1a")} testid="faq-1" />
            <FAQItem q={t("home.faq2q")} a={t("home.faq2a")} testid="faq-2" />
            <FAQItem q={t("home.faq3q")} a={t("home.faq3a")} testid="faq-3" />
            <FAQItem q={t("home.faq4q")} a={t("home.faq4a")} testid="faq-4" />
          </div>
        </div>
      </section>

      {/* ========= FINAL CTA (compact) ========= */}
      <section className="pt-6 pb-14">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="bg-gradient-to-br from-coral to-coral-deep text-white rounded-[2.5rem] px-8 py-10 lg:px-12 lg:py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="overline !text-white/70 mb-2">{t("home.finalEyebrow", "Ready?")}</div>
              <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight" data-testid="final-cta-title">
                {t("home.ctaBandTitle")}
              </h2>
            </div>
            <Link to="/plans" className="btn bg-white text-coral-deep shadow-pop hover:bg-white/95 text-base md:text-lg whitespace-nowrap group" data-testid="final-cta">
              {t("home.cta")}
              <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
